import { ArrowUpRight, CalendarClock, Target, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import {
  DESEMPENO_LABELS,
  ESTADO_TONES,
  estadoLabel,
  PRIORIDAD_TONES,
  prioridadLabel,
  responsable,
  tipoLabel,
  TONO_A_BADGE,
} from '../lib/metas-format';
import type { Goal } from '../metas.types';
import { MetaProgress } from './meta-progress';

interface MetaCardProps {
  goal: Goal;
  className?: string;
}

/** Tarjeta resumen de una meta con su avance y accesos. */
export function MetaCard({ goal, className }: MetaCardProps) {
  const desempeno = goal.performance_status ?? 'on_track';

  return (
    <Card className={cn('flex min-w-0 flex-col gap-3 p-5', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/metas/${goal.id}`}
            className="block truncate text-sm font-semibold text-ink-900 transition-colors hover:text-brand-700"
          >
            {goal.goal_name}
          </Link>

          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-ink-400">
            <span className="inline-flex items-center gap-1">
              <Target className="size-3" aria-hidden="true" />
              {tipoLabel(goal.goal_type)}
            </span>
            <span className="inline-flex items-center gap-1">
              <UserRound className="size-3" aria-hidden="true" />
              {responsable(goal)}
            </span>
            {goal.fuel_grade_name ? <span>{goal.fuel_grade_name}</span> : null}
          </p>
        </div>

        <span className="flex shrink-0 flex-col items-end gap-1">
          <Badge variant={TONO_A_BADGE[ESTADO_TONES[goal.status]]}>{estadoLabel(goal.status)}</Badge>
          <Badge variant={TONO_A_BADGE[PRIORIDAD_TONES[goal.priority]]}>
            {prioridadLabel(goal.priority)}
          </Badge>
        </span>
      </div>

      <MetaProgress goal={goal} />

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-ink-100 pt-3 text-[11px] text-ink-400">
        <span className="inline-flex items-center gap-1.5">
          <CalendarClock className="size-3" aria-hidden="true" />
          {DESEMPENO_LABELS[desempeno]}
        </span>

        <Link
          to={`/metas/${goal.id}`}
          className="inline-flex items-center gap-1 font-semibold text-brand-600 transition-colors hover:text-brand-700"
        >
          Ver detalle
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>
    </Card>
  );
}
